import { useRef, useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * TagCloud Component - 2026 UX/UI Design
 * Features:
 * - Weighted tag sizing based on photo count
 * - Horizontal scrollable category chips (YouTube-style)
 * - Smooth selection animations
 * - Clear all button with count
 */

// Category configuration with icons and colors
const CATEGORIES = {
  all: { label: 'All Tags', icon: '🔍', gradient: 'from-slate-800 to-slate-700' },
  facial_attributes: { label: 'Facial', icon: '😊', gradient: 'from-amber-500 to-amber-600' },
  emotions: { label: 'Emotions', icon: '💫', gradient: 'from-pink-500 to-pink-600' },
  activities: { label: 'Activities', icon: '🎯', gradient: 'from-orange-500 to-orange-600' },
  people_groups: { label: 'People', icon: '👥', gradient: 'from-purple-500 to-purple-600' },
  event_scenes: { label: 'Scenes', icon: '🏢', gradient: 'from-cyan-500 to-cyan-600' },
  event_objects: { label: 'Objects', icon: '🎤', gradient: 'from-emerald-500 to-emerald-600' },
  atmosphere: { label: 'Atmosphere', icon: '✨', gradient: 'from-indigo-500 to-indigo-600' },
};

export function TagCloud({
  allTags = [],
  categorizedTags = {},
  selectedTags = [],
  selectedCategory = 'all',
  tagCounts = {}, // { tagName: photoCount }
  onTagSelect,
  onTagDeselect,
  onCategoryChange,
  onClearAll,
  className = '',
}) {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll capability
  useEffect(() => {
    const checkScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
          container.scrollLeft < container.scrollWidth - container.clientWidth - 1
        );
      }
    };

    checkScroll();
    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      container?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  // Scroll handlers
  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
  };

  // Get tags for current category
  const displayTags = selectedCategory === 'all'
    ? allTags
    : categorizedTags[selectedCategory] || [];

  // Calculate tag size based on count (for weighted display)
  const getTagSize = (tag) => {
    const count = tagCounts[tag] || 1;
    const maxCount = Math.max(...Object.values(tagCounts), 1);
    const ratio = count / maxCount;

    if (ratio > 0.7) return 'text-base font-semibold';
    if (ratio > 0.4) return 'text-sm font-medium';
    return 'text-sm';
  };

  const handleTagClick = (tag) => {
    if (selectedTags.includes(tag)) {
      onTagDeselect?.(tag);
    } else {
      onTagSelect?.(tag);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Category Chips - Horizontal Scrollable */}
      <div className="relative">
        {/* Left Scroll Button */}
        {canScrollLeft && (
          <button
            onClick={scrollLeft}
            className="
              absolute left-0 top-1/2 -translate-y-1/2 z-10
              w-8 h-8 flex items-center justify-center
              bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm
              rounded-full shadow-lg border border-slate-200 dark:border-slate-700
              hover:bg-white dark:hover:bg-slate-800
              transition-all duration-200
            "
          >
            <ChevronLeft className="w-4 h-4 text-slate-600 dark:text-slate-300" />
          </button>
        )}

        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          className="
            flex gap-2 overflow-x-auto scrollbar-hide
            py-2 px-1
            scroll-smooth
          "
        >
          {Object.entries(CATEGORIES).map(([key, { label, icon, gradient }]) => {
            const isSelected = selectedCategory === key;
            const tagCount = key === 'all'
              ? allTags.length
              : (categorizedTags[key]?.length || 0);

            return (
              <button
                key={key}
                onClick={() => onCategoryChange?.(key)}
                className={`
                  flex-shrink-0 flex items-center gap-2
                  px-4 py-2.5 rounded-xl
                  font-medium text-sm
                  transition-all duration-300 ease-out
                  ${isSelected
                    ? `bg-gradient-to-r ${gradient} text-white shadow-lg scale-105`
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:scale-105'
                  }
                `}
              >
                <span className="text-base">{icon}</span>
                <span>{label}</span>
                <span className={`
                  px-1.5 py-0.5 rounded-full text-xs
                  ${isSelected
                    ? 'bg-white/20 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                  }
                `}>
                  {tagCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Right Scroll Button */}
        {canScrollRight && (
          <button
            onClick={scrollRight}
            className="
              absolute right-0 top-1/2 -translate-y-1/2 z-10
              w-8 h-8 flex items-center justify-center
              bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm
              rounded-full shadow-lg border border-slate-200 dark:border-slate-700
              hover:bg-white dark:hover:bg-slate-800
              transition-all duration-200
            "
          >
            <ChevronRight className="w-4 h-4 text-slate-600 dark:text-slate-300" />
          </button>
        )}
      </div>

      {/* Selected Tags Summary */}
      {selectedTags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap p-3 bg-gradient-to-r from-[#BF212F]/5 to-[#F26B38]/5 rounded-xl border border-[#BF212F]/20">
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
            Selected:
          </span>
          <div className="flex flex-wrap gap-1.5">
            {selectedTags.map((tag) => (
              <span
                key={tag}
                className="
                  inline-flex items-center gap-1
                  px-2.5 py-1 rounded-full
                  text-xs font-semibold
                  bg-gradient-to-r from-[#BF212F] to-[#F26B38]
                  text-white
                  animate-scale-in
                "
              >
                {tag}
                <button
                  onClick={() => onTagDeselect?.(tag)}
                  className="ml-0.5 hover:bg-white/20 rounded-full p-0.5 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="ml-auto text-[#BF212F] hover:text-[#8B1923] hover:bg-[#BF212F]/10 rounded-lg"
          >
            Clear all ({selectedTags.length})
          </Button>
        </div>
      )}

      {/* Tags Grid */}
      <div className="
        bg-gradient-to-br from-slate-50 to-white
        dark:from-slate-800 dark:to-slate-900
        rounded-2xl border-2 border-slate-200 dark:border-slate-700
        p-4
      ">
        <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto scrollbar-hide p-1">
          {displayTags.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 w-full text-center py-8">
              No tags available in this category
            </p>
          ) : (
            displayTags.map((tag, index) => {
              const isSelected = selectedTags.includes(tag);
              const sizeClass = getTagSize(tag);

              return (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className={`
                    px-4 py-2 rounded-xl
                    ${sizeClass}
                    transition-all duration-200 ease-out
                    ${isSelected
                      ? 'bg-gradient-to-r from-[#BF212F] to-[#F26B38] text-white shadow-lg scale-105'
                      : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-600 hover:border-[#BF212F]/50 hover:shadow-md hover:scale-105'
                    }
                  `}
                  style={{ animationDelay: `${Math.min(index, 20) * 0.02}s` }}
                >
                  {tag}
                  {tagCounts[tag] && (
                    <span className={`
                      ml-1.5 text-xs
                      ${isSelected ? 'text-white/80' : 'text-slate-400'}
                    `}>
                      ({tagCounts[tag]})
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default TagCloud;
